import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MuiButton from "@mui/material/Button";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useAvailableLanguagesSetting } from "@/hooks/useSetting";
import {
  Term,
  TermCreatePayload,
  TermLanguage,
  TermUpdatePayload,
} from "@/domain/terms.interface";
import termValidationSchema, { TermFormData } from "./validation.schema";

type TermModalProps = {
  open: boolean;
  onClose: () => void;
  term?: Term;
  onSave: (
    data: TermCreatePayload | TermUpdatePayload,
    isEdit: boolean
  ) => Promise<void>;
  isPending: boolean;
};

const EMPTY_LANGUAGE = {
  language: "",
  title: "",
  content: "",
  fullContentUrl: "",
  fullContentLabel: "",
};

const DEFAULT_VALUES: TermFormData = {
  revision: "",
  languages: [EMPTY_LANGUAGE],
};

const TermModal: FC<TermModalProps> = ({
  open,
  onClose,
  term,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!term;

  const availableLanguages = useAvailableLanguagesSetting();
  const languageOptions = useMemo(
    () => availableLanguages.map((code) => ({ key: code, value: code })),
    [availableLanguages]
  );

  const methods = useForm<TermFormData>({
    resolver: yupResolver(termValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "languages",
  });

  useEffect(() => {
    if (term) {
      reset({
        revision: term.revision,
        languages:
          term.languages && term.languages.length > 0
            ? term.languages.map((l) => ({
                _id: l._id,
                language: l.language,
                title: l.title,
                content: l.content,
                fullContentUrl: l.fullContentUrl,
                fullContentLabel: l.fullContentLabel,
              }))
            : [EMPTY_LANGUAGE],
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [term, reset]);

  const onSubmit = async (data: TermFormData) => {
    const languages: TermLanguage[] = (data.languages || []).map((l) => ({
      ...(l._id ? { _id: l._id } : {}),
      language: l.language,
      title: (l.title || "").trim(),
      content: (l.content || "").trim(),
      fullContentUrl: (l.fullContentUrl || "").trim(),
      fullContentLabel: (l.fullContentLabel || "").trim(),
    }));

    if (isEdit) {
      const payload: TermUpdatePayload = {
        revision: data.revision.trim(),
        languages,
      };
      await onSave(payload, true);
    } else {
      const payload: TermCreatePayload = {
        revision: data.revision.trim(),
        languages: languages.map((l) => ({
          language: l.language,
          title: l.title,
          content: l.content,
          fullContentUrl: l.fullContentUrl,
          fullContentLabel: l.fullContentLabel,
        })),
        createdBy: userInfo?._id as string,
      };
      await onSave(payload, false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit Term" : "New term"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 600,
        maxHeight: 780,
        maxWidth: 880,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="revision"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="term-revision"
                      label="Revision"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.revision}
                      helperText={
                        errors.revision ? errors.revision.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="titleSmall">Languages *</Typography>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => append(EMPTY_LANGUAGE)}
                  >
                    Add language
                  </MuiButton>
                </Box>
                {typeof errors.languages?.message === "string" && (
                  <Typography
                    variant="bodySmall"
                    sx={{ color: theme.palette.error.main, mb: 1 }}
                  >
                    {errors.languages.message}
                  </Typography>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {fields.map((langField, index) => {
                    const langErrors = errors.languages?.[index];
                    return (
                      <Paper
                        key={langField.id}
                        variant="outlined"
                        sx={{ p: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1.5,
                          }}
                        >
                          <Typography
                            variant="bodyMedium"
                            sx={{ fontWeight: 600 }}
                          >
                            Language #{index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            disabled={fields.length <= 1}
                            onClick={() => remove(index)}
                            aria-label="remove language"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Grid container spacing={"16px"}>
                          <Grid item xs={12} sm={4}>
                            <Controller
                              name={`languages.${index}.language` as const}
                              control={control}
                              render={({ field }) => (
                                <Select
                                  handleChange={(value) =>
                                    field.onChange(value)
                                  }
                                  data={languageOptions}
                                  value={field.value as string}
                                  label="Language"
                                  id={`term-lang-${index}`}
                                  required
                                  error={!!langErrors?.language}
                                  errorMessage={
                                    langErrors?.language?.message || ""
                                  }
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <Controller
                              name={`languages.${index}.title` as const}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  id={`term-title-${index}`}
                                  label="Title"
                                  variant="outlined"
                                  required
                                  value={field.value}
                                  onChange={(value) => field.onChange(value)}
                                  error={!!langErrors?.title}
                                  helperText={
                                    langErrors?.title?.message || null
                                  }
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Controller
                              name={`languages.${index}.content` as const}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  id={`term-content-${index}`}
                                  label="Content"
                                  variant="outlined"
                                  required
                                  value={field.value}
                                  onChange={(value) => field.onChange(value)}
                                  error={!!langErrors?.content}
                                  helperText={
                                    langErrors?.content?.message || null
                                  }
                                  multiline
                                  minRows={3}
                                  maxRows={6}
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <Controller
                              name={
                                `languages.${index}.fullContentUrl` as const
                              }
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  id={`term-url-${index}`}
                                  label="Full content URL"
                                  variant="outlined"
                                  required
                                  value={field.value}
                                  onChange={(value) => field.onChange(value)}
                                  error={!!langErrors?.fullContentUrl}
                                  helperText={
                                    langErrors?.fullContentUrl?.message || null
                                  }
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Controller
                              name={
                                `languages.${index}.fullContentLabel` as const
                              }
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  id={`term-label-${index}`}
                                  label="Full content label"
                                  variant="outlined"
                                  required
                                  value={field.value}
                                  onChange={(value) => field.onChange(value)}
                                  error={!!langErrors?.fullContentLabel}
                                  helperText={
                                    langErrors?.fullContentLabel?.message ||
                                    null
                                  }
                                  fullWidth
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default TermModal;
